// src/pages/Leite/propriedade/UltimaAnaliseCard.jsx
import React from 'react';
import { Box, Chip, Divider, Link, Paper, Skeleton, Stack, Typography, alpha, useTheme } from '@mui/material';
import * as Lucide from 'lucide-react';
import { formatarContagem, formatarDataHora, formatarLog10, formatarVariacao } from '../../../utils/coleta';
import { AZUL_CLARO } from '../../../theme/cores';
import { AssistenteFaixa } from './Assistente';

const dois = (n) => String(n).padStart(2, '0');
const diaMes = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : `${dois(d.getDate())}/${dois(d.getMonth() + 1)}`;
};

/**
 * "Qualidade do leite — última análise": a última CBT com resultado, a variação contra a
 * coleta anterior do mesmo ponto e origem (com a data dela) e, embaixo, o que o
 * assistente tem a dizer sobre ela.
 */
export default function UltimaAnaliseCard({ ultima, nomePonto, unidade, rotuloMedida, sugestao, carregando, configurada, onAbrir }) {
    const theme = useTheme();
    const log10 = ultima?.experimento?.log10Contagem;
    const variacao = ultima?.variacaoPercentual;
    const alta = Number.isFinite(variacao) && variacao > 0;
    const corVariacao = alta ? theme.palette.warning.dark : theme.palette.success.main;

    return (
        <Paper
            variant="outlined"
            sx={{ height: '100%', borderRadius: 3, overflow: 'hidden', borderTop: `3px solid ${AZUL_CLARO}`, display: 'flex', flexDirection: 'column' }}
        >
            <Box sx={{ p: { xs: 2.5, sm: 3 }, flex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
                        <Lucide.Milk size={20} color={AZUL_CLARO} style={{ flexShrink: 0 }} />
                        <Typography variant="overline" fontWeight={700} sx={{ letterSpacing: 1.5, lineHeight: 1.4 }}>
                            Qualidade do leite — última análise
                        </Typography>
                    </Stack>
                    <Chip size="small" label={`Última ${rotuloMedida}`} sx={{ fontWeight: 600, flexShrink: 0 }} />
                </Stack>

                {carregando ? (
                    <Box sx={{ mt: 3 }} aria-busy="true" aria-label="Carregando a última análise">
                        <Skeleton width="55%" />
                        <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mt: 0.5 }}>
                            <Skeleton variant="rounded" width={170} height={52} sx={{ borderRadius: 1.5 }} />
                            <Skeleton width={90} />
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.5 }}>
                            <Skeleton width="40%" sx={{ maxWidth: 160 }} />
                            <Skeleton width="40%" sx={{ maxWidth: 150 }} />
                        </Stack>
                    </Box>
                ) : !configurada ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                        A página do leite ainda não foi configurada. Peça ao administrador para escolher a análise dela.
                    </Typography>
                ) : !ultima ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                        Nenhuma análise com resultado ainda. Registre uma coleta na página do leite.
                    </Typography>
                ) : (
                    <Box sx={{ mt: 3 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: AZUL_CLARO }} />
                            <Typography variant="caption" color="text.secondary">
                                Resultado do ensaio · {[nomePonto(ultima.coleta?.pontoColetaId), formatarDataHora(new Date(ultima.coleta?.dataHora))].filter(Boolean).join(' · ')}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" sx={{ mt: 0.5 }}>
                            <Typography fontWeight={800} sx={{ fontSize: { xs: '2.4rem', sm: '3rem' }, lineHeight: 1.1 }}>
                                {formatarContagem(log10)}
                            </Typography>
                            <Typography color="text.secondary">{unidade}</Typography>
                            <Typography variant="body2" color="text.secondary">· {formatarLog10(log10)}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1.5 }}>
                            {Number.isFinite(variacao) ? (
                                <>
                                    {alta ? <Lucide.TrendingUp size={16} color={corVariacao} /> : <Lucide.TrendingDown size={16} color={corVariacao} />}
                                    <Typography variant="body2" fontWeight={700} sx={{ color: corVariacao }}>
                                        {formatarVariacao(variacao)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        vs. coleta {ultima.anteriorDataHora ? `de ${diaMes(ultima.anteriorDataHora)}` : 'anterior'}
                                    </Typography>
                                </>
                            ) : (
                                <Typography variant="body2" color="text.secondary">Primeira coleta deste ponto e origem.</Typography>
                            )}
                            <Box sx={{ flex: 1 }} />
                            <Link component="button" type="button" underline="hover" onClick={() => onAbrir(ultima)}
                                sx={{ fontWeight: 700, fontSize: '0.875rem', color: AZUL_CLARO }}>
                                Ver amostra {ultima.identificacao}
                            </Link>
                        </Stack>
                    </Box>
                )}
            </Box>
            {carregando ? (
                <>
                    <Divider sx={{ borderColor: alpha(AZUL_CLARO, 0.15) }} />
                    <Box sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: alpha(AZUL_CLARO, 0.05), display: 'flex', gap: 1.5 }}>
                        <Skeleton variant="circular" width={48} height={48} sx={{ flexShrink: 0 }} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton width={150} />
                            <Skeleton width="100%" />
                            <Skeleton width="80%" />
                        </Box>
                    </Box>
                </>
            ) : configurada && ultima && (
                <>
                    <Divider sx={{ borderColor: alpha(AZUL_CLARO, 0.15) }} />
                    <AssistenteFaixa sugestao={sugestao} />
                </>
            )}
        </Paper>
    );
}
